import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0]?.slug || '');
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = getCategoryBySlug(activeSlug);
  const currentArticles = getArticlesByCategory(activeSlug);

  const handleCategoryChange = useCallback((newSlug: string) => {
    setActiveSlug(newSlug);
    setMobileCatOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  const collectionSchema = currentCategory ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory.name,
    description: currentCategory.description,
    url: `https://www.cuimianxingfu.com/category/${currentCategory.slug}`,
    inLanguage: 'zh-Hans',
    isPartOf: {
      '@type': 'WebSite',
      name: '催眠性福生活',
      url: 'https://www.cuimianxingfu.com',
    },
  } : null;

  return (
    <>
      <ScrollToTop />
      {collectionSchema && <SchemaOrg data={collectionSchema} />}
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Page Header */}
            <div className="py-4 md:py-6">
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 md:gap-8 pb-12 md:pb-16">
              {/* Mobile Category Selector */}
              <div className="lg:hidden">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 text-sm font-medium text-foreground-700 cursor-pointer"
                >
                  <span>{currentCategory?.name}</span>
                  {mobileCatOpen ? (
                    <i className="ri-arrow-up-s-line"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line"></i>
                  )}
                </button>
                {mobileCatOpen && (
                  <div className="mt-2 bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          activeSlug === cat.slug
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-foreground-600 hover:bg-background-200/50'
                        }`}
                      >
                        {cat.name}
                        <span className="text-foreground-400 ml-2">({cat.articleCount})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop Sidebar */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <nav className="sticky top-24 bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                  <div className="px-4 py-3 border-b border-background-200/70">
                    <span className="text-sm font-semibold text-foreground-800">文章分类</span>
                  </div>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        activeSlug === cat.slug
                          ? 'bg-primary-50 text-primary-600 font-medium border-l-2 border-primary-500'
                          : 'text-foreground-600 hover:bg-background-200/50 border-l-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{cat.name}</span>
                        <span className="text-xs text-foreground-400">{cat.articleCount}</span>
                      </div>
                    </button>
                  ))}
                </nav>
              </aside>

              {/* Article List */}
              <div className="flex-1 min-w-0">
                {currentArticles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                    {currentArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group block bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all overflow-hidden cursor-pointer"
                      >
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-1.5 text-xs text-foreground-400 mb-2 flex-wrap">
                            <span>{article.date}</span>
                            <span>·</span>
                            <span>{article.readTime}</span>
                            <span>·</span>
                            <span>{article.author}</span>
                          </div>
                          <h2 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h2>
                          <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {article.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs whitespace-nowrap"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}